import { create } from 'zustand';

import type {
  WeaponAttachOffset,
  WeaponAudio,
  WeaponAudioLayer,
  WeaponDocument,
  WeaponPartFinish,
  WeaponPartRole,
  WeaponSkin,
} from '../../../engine/content/schema/weapon';
import { getActiveProjectId } from '../../../engine/editor/persistence/ProjectPersistence';
import { getWeaponPersistence, WeaponPublishValidationError } from './weaponPersistence';

// Mirrors src/engine/editor/store/mapEditorStore.ts's shape: authored-document
// state with a source* dirty-check twin, an undo/redo snapshot stack, a
// saveState enum, and injected transport-neutral persistence. The one
// structural difference is that a creator's weapons are a roster (many
// independently named/saved/published documents), not one always-current
// document - see contracts/studio-editor-studio-api.md - so the working
// state is `weapons: WeaponDocument[]` plus `activeWeaponId`, and undo/redo
// entries snapshot one weapon's whole document rather than a set of arrays.

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';
export type AudioLayerKey = 'crack' | 'body' | 'tail' | 'swing' | 'impact';

const UNDO_LIMIT = 64;

interface UndoEntry {
  weaponId: string;
  snapshot: WeaponDocument;
  label: string;
}

interface GunsmithState {
  // Roster
  weapons: WeaponDocument[];
  sourceWeapons: WeaponDocument[];
  activeWeaponId: string | null;
  rosterLoaded: boolean;
  /** Project id the currently-loaded roster belongs to, so a project switch
   * (a client-side navigation, no page reload - see loadRoster) can tell a
   * stale `rosterLoaded: true` left over from the previous project apart
   * from a genuinely up-to-date roster for the current one. */
  rosterProjectId: string | null;

  // Undo / redo (each entry is scoped to the weapon it was captured from)
  undoStack: UndoEntry[];
  redoStack: UndoEntry[];

  // Save / publish
  saveState: SaveState;
  statusMessage: string | null;
  /** Ids of weapons whose currently-saved state has been published at least once. */
  publishedWeaponIds: string[];

  // Assembly UI selection
  selectedSocket: string | null;

  // Hydration / loading
  hydrateFromRoster: (weapons: WeaponDocument[]) => void;
  loadRoster: () => Promise<void>;

  // Roster management
  createWeapon: (name: string, archetype: string, chassisId: string) => string;
  duplicateWeapon: (weaponId: string) => string | null;
  /**
   * Remove a weapon from the working roster. A weapon that has never been
   * saved (absent from `sourceWeapons`) is only ever local state, so it is
   * just dropped. A saved weapon is removed from the roster optimistically,
   * then deleted server-side via persistence; a failure rolls the roster
   * back to include it again rather than leaving the UI silently out of
   * sync with the server.
   */
  deleteWeapon: (weaponId: string) => Promise<void>;
  setActiveWeaponId: (id: string | null) => void;

  // Meta
  renameActiveWeapon: (name: string) => void;
  setActiveArchetype: (archetype: string) => void;
  setActiveChassisId: (chassisId: string) => void;

  // Assembly
  setSelectedSocket: (socket: string | null) => void;
  mountComponent: (socket: string, componentId: string) => void;
  unmountComponent: (socket: string) => void;
  setOffsetOverride: (socket: string, offset: WeaponAttachOffset | undefined) => void;

  // Skin / material
  setSkinPartFinish: (role: WeaponPartRole, finish: WeaponPartFinish | undefined) => void;
  renameActiveSkin: (name: string) => void;
  applySkin: (skin: WeaponSkin) => void;

  // Audio
  setAudioLayer: (layer: AudioLayerKey, value: WeaponAudioLayer | undefined) => void;
  setReverbSend: (value: number | undefined) => void;

  // Undo / redo
  undo: () => void;
  redo: () => void;

  // Persistence
  saveActiveWeapon: () => Promise<void>;
  publishActiveWeapon: () => Promise<void>;
  resetActiveToSource: () => void;
  clearStatus: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function cloneOffset(offset: WeaponAttachOffset): WeaponAttachOffset {
  return {
    position: [...offset.position] as [number, number, number],
    rotation: [...offset.rotation] as [number, number, number],
    scale: offset.scale,
  };
}

function cloneAudioLayer(layer: WeaponAudioLayer): WeaponAudioLayer {
  return { ...layer };
}

function cloneAudio(audio: WeaponAudio): WeaponAudio {
  return {
    crack: audio.crack ? cloneAudioLayer(audio.crack) : undefined,
    body: audio.body ? cloneAudioLayer(audio.body) : undefined,
    tail: audio.tail ? cloneAudioLayer(audio.tail) : undefined,
    reverbSend: audio.reverbSend,
  };
}

function cloneSkin(skin: WeaponSkin): WeaponSkin {
  const parts: WeaponSkin['parts'] = {};
  for (const [role, finish] of Object.entries(skin.parts)) {
    if (finish) parts[role as WeaponPartRole] = { ...finish };
  }
  return { id: skin.id, name: skin.name, parts };
}

export function cloneWeapon(weapon: WeaponDocument): WeaponDocument {
  return {
    ...weapon,
    components: weapon.components.map((mount) => ({
      ...mount,
      offsetOverride: mount.offsetOverride ? cloneOffset(mount.offsetOverride) : undefined,
    })),
    skin: cloneSkin(weapon.skin),
    audio: weapon.audio ? cloneAudio(weapon.audio) : undefined,
  };
}

function cloneWeapons(weapons: WeaponDocument[]): WeaponDocument[] {
  return weapons.map(cloneWeapon);
}

function offsetsEqual(a?: WeaponAttachOffset, b?: WeaponAttachOffset): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.position[0] === b.position[0] && a.position[1] === b.position[1] && a.position[2] === b.position[2] &&
    a.rotation[0] === b.rotation[0] && a.rotation[1] === b.rotation[1] && a.rotation[2] === b.rotation[2] &&
    a.scale === b.scale
  );
}

function weaponsEqual(a: WeaponDocument, b: WeaponDocument): boolean {
  if (a.id !== b.id || a.name !== b.name || a.archetype !== b.archetype || a.chassisId !== b.chassisId) return false;
  if (a.components.length !== b.components.length) return false;
  for (let i = 0; i < a.components.length; i += 1) {
    const ma = a.components[i]!;
    const mb = b.components[i]!;
    if (ma.socket !== mb.socket || ma.componentId !== mb.componentId) return false;
    if (!offsetsEqual(ma.offsetOverride, mb.offsetOverride)) return false;
  }
  if (a.skin.id !== b.skin.id || a.skin.name !== b.skin.name) return false;
  const rolesA = Object.keys(a.skin.parts);
  const rolesB = Object.keys(b.skin.parts);
  if (rolesA.length !== rolesB.length) return false;
  for (const role of rolesA) {
    const fa = a.skin.parts[role as WeaponPartRole];
    const fb = b.skin.parts[role as WeaponPartRole];
    if (!fa || !fb) return false;
    if (fa.color !== fb.color || fa.roughness !== fb.roughness || fa.metalness !== fb.metalness ||
        fa.emissive !== fb.emissive || fa.emissiveIntensity !== fb.emissiveIntensity) return false;
  }
  const aa = a.audio;
  const ab = b.audio;
  if (!!aa !== !!ab) return false;
  if (aa && ab) {
    for (const key of ['crack', 'body', 'tail'] as const) {
      const la = aa[key];
      const lb = ab[key];
      if (!!la !== !!lb) return false;
      if (la && lb && (la.sampleId !== lb.sampleId || la.gain !== lb.gain || la.pitchJitter !== lb.pitchJitter)) return false;
    }
    if (aa.reverbSend !== ab.reverbSend) return false;
  }
  return true;
}

/** Whether the active weapon (or any weapon, on roster-shape changes) differs from its saved source. */
export function isWeaponDirty(weapons: WeaponDocument[], sourceWeapons: WeaponDocument[]): boolean {
  if (weapons.length !== sourceWeapons.length) return true;
  const sourceById = new Map(sourceWeapons.map((w) => [w.id, w]));
  for (const weapon of weapons) {
    const source = sourceById.get(weapon.id);
    if (!source || !weaponsEqual(weapon, source)) return true;
  }
  return false;
}

function makeWeaponId(): string {
  return `weapon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Whether `projectId` is no longer the active project - a request for it is
 * stale and its result should be dropped. `getActiveProjectId()` throws
 * once there is no active project at all (e.g. the editor was left entirely
 * while the request was in flight), which is itself a "yes, stale" case. */
function isStaleProject(projectId: string): boolean {
  try {
    return getActiveProjectId() !== projectId;
  } catch {
    return true;
  }
}

function pushUndoPatch(
  state: { undoStack: UndoEntry[]; redoStack: UndoEntry[] },
  weaponId: string,
  snapshot: WeaponDocument,
  label: string,
): { undoStack: UndoEntry[]; redoStack: UndoEntry[] } {
  const entry: UndoEntry = { weaponId, snapshot: cloneWeapon(snapshot), label };
  return {
    undoStack: [entry, ...state.undoStack].slice(0, UNDO_LIMIT),
    redoStack: [],
  };
}

/** Applies `updater` to the active weapon and records an undo entry from its pre-update state. */
function updateActive(
  get: () => GunsmithState,
  set: (patch: Partial<GunsmithState>) => void,
  label: string,
  updater: (weapon: WeaponDocument) => WeaponDocument,
) {
  const state = get();
  const active = state.weapons.find((w) => w.id === state.activeWeaponId);
  if (!active) return;
  const undoPatch = pushUndoPatch(state, active.id, active, label);
  const nextWeapon = updater(cloneWeapon(active));
  set({
    ...undoPatch,
    weapons: state.weapons.map((w) => (w.id === active.id ? nextWeapon : w)),
    saveState: 'idle',
  });
}

// ── Store ────────────────────────────────────────────────────────────────────

export const useGunsmithStore = create<GunsmithState>((set, get) => ({
  weapons: [],
  sourceWeapons: [],
  activeWeaponId: null,
  rosterLoaded: false,
  rosterProjectId: null,

  undoStack: [],
  redoStack: [],

  saveState: 'idle',
  statusMessage: null,
  publishedWeaponIds: [],

  selectedSocket: null,

  hydrateFromRoster: (weapons) => {
    set({
      weapons: cloneWeapons(weapons),
      sourceWeapons: cloneWeapons(weapons),
      activeWeaponId: weapons[0]?.id ?? null,
      rosterLoaded: true,
      undoStack: [],
      redoStack: [],
      selectedSocket: null,
      saveState: 'idle',
      statusMessage: null,
    });
  },

  loadRoster: async () => {
    // Captured up front, not re-read after the await: the active project can
    // change mid-request (a creator switching projects is a client-side
    // navigation, not a page reload, so this store's state outlives it).
    // Both the success and error paths below re-check it against the live
    // active project before touching state, so a request that is no longer
    // for the current project is silently dropped rather than clobbering
    // whatever the current project has already loaded.
    let projectId: string;
    try {
      projectId = getActiveProjectId();
    } catch {
      return; // No active project (the editor was already left) - nothing to load.
    }
    set({ saveState: 'saving', statusMessage: 'Loading your weapons...' });
    try {
      const response = await getWeaponPersistence().listWeapons(projectId);
      if (isStaleProject(projectId)) return;
      get().hydrateFromRoster(response.weapons);
      set({ saveState: 'idle', statusMessage: null, publishedWeaponIds: response.publishedWeaponIds, rosterProjectId: projectId });
    } catch (error) {
      if (isStaleProject(projectId)) return;
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to load your weapons.',
      });
    }
  },

  createWeapon: (name, archetype, chassisId) => {
    const id = makeWeaponId();
    const weapon: WeaponDocument = {
      id,
      name,
      archetype,
      chassisId,
      components: [],
      skin: { id: `${id}_skin`, name: 'Untitled skin', parts: {} },
      audio: undefined,
    };
    set((state) => ({
      weapons: [...state.weapons, weapon],
      activeWeaponId: id,
      selectedSocket: null,
      statusMessage: `Created "${name}".`,
      saveState: 'idle',
    }));
    return id;
  },

  duplicateWeapon: (weaponId) => {
    const state = get();
    const source = state.weapons.find((w) => w.id === weaponId);
    if (!source) return null;
    const id = makeWeaponId();
    const copy = cloneWeapon(source);
    copy.id = id;
    copy.name = `${source.name} copy`;
    copy.skin = { ...copy.skin, id: `${id}_skin` };
    set({
      weapons: [...state.weapons, copy],
      activeWeaponId: id,
      selectedSocket: null,
      statusMessage: `Duplicated "${source.name}".`,
      saveState: 'idle',
    });
    return id;
  },

  deleteWeapon: async (weaponId) => {
    const state = get();
    const sourceWeapon = state.sourceWeapons.find((w) => w.id === weaponId);

    const remainingWeapons = state.weapons.filter((w) => w.id !== weaponId);
    const nextActiveWeaponId = state.activeWeaponId === weaponId ? (remainingWeapons[0]?.id ?? null) : state.activeWeaponId;

    if (!sourceWeapon) {
      // Never saved - only ever local state, so a plain removal is honest;
      // there is nothing on the server to delete.
      set({
        weapons: remainingWeapons,
        activeWeaponId: nextActiveWeaponId,
        undoStack: state.undoStack.filter((e) => e.weaponId !== weaponId),
        redoStack: state.redoStack.filter((e) => e.weaponId !== weaponId),
        selectedSocket: null,
        statusMessage: 'Removed weapon.',
        saveState: 'idle',
      });
      return;
    }

    // Saved weapon: remove from the working roster optimistically, then
    // delete server-side. A failure rolls the removal back rather than
    // leaving the UI showing a weapon as gone when it still exists on the
    // server. The *working* copy (which may hold unsaved edits) is kept
    // aside so a rejected delete restores exactly what was there before the
    // click - a true no-op - rather than silently reverting to the last-
    // saved version and losing whatever the creator had not saved yet.
    const workingWeapon = state.weapons.find((w) => w.id === weaponId) ?? sourceWeapon;
    const undoEntries = state.undoStack.filter((e) => e.weaponId === weaponId);
    const redoEntries = state.redoStack.filter((e) => e.weaponId === weaponId);
    const wasPublished = state.publishedWeaponIds.includes(weaponId);
    set({
      weapons: remainingWeapons,
      sourceWeapons: state.sourceWeapons.filter((w) => w.id !== weaponId),
      activeWeaponId: nextActiveWeaponId,
      publishedWeaponIds: state.publishedWeaponIds.filter((id) => id !== weaponId),
      undoStack: state.undoStack.filter((e) => e.weaponId !== weaponId),
      redoStack: state.redoStack.filter((e) => e.weaponId !== weaponId),
      selectedSocket: null,
      saveState: 'saving',
      statusMessage: 'Deleting...',
    });

    try {
      await getWeaponPersistence().deleteWeapon(getActiveProjectId(), weaponId);
      set({ saveState: 'idle', statusMessage: `Deleted "${sourceWeapon.name}".` });
    } catch (error) {
      set((s) => ({
        weapons: [...s.weapons, cloneWeapon(workingWeapon)],
        sourceWeapons: [...s.sourceWeapons, cloneWeapon(sourceWeapon)],
        // Only reclaim focus onto the restored weapon if the selection is
        // still exactly where the optimistic removal left it - if the
        // creator has since clicked onto a different weapon while the
        // delete was in flight, respect that instead of yanking focus back.
        activeWeaponId: s.activeWeaponId === nextActiveWeaponId ? weaponId : s.activeWeaponId,
        publishedWeaponIds: wasPublished ? [...s.publishedWeaponIds, weaponId] : s.publishedWeaponIds,
        undoStack: [...undoEntries, ...s.undoStack].slice(0, UNDO_LIMIT),
        redoStack: [...redoEntries, ...s.redoStack].slice(0, UNDO_LIMIT),
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to delete.',
      }));
    }
  },

  setActiveWeaponId: (id) => set({ activeWeaponId: id, selectedSocket: null }),

  renameActiveWeapon: (name) => updateActive(get, set, 'Rename', (w) => ({ ...w, name })),
  setActiveArchetype: (archetype) => updateActive(get, set, 'Change archetype', (w) => ({ ...w, archetype })),
  setActiveChassisId: (chassisId) => updateActive(get, set, 'Change chassis', (w) => ({ ...w, chassisId, components: [] })),

  setSelectedSocket: (socket) => set({ selectedSocket: socket }),

  mountComponent: (socket, componentId) => {
    updateActive(get, set, 'Mount component', (w) => ({
      ...w,
      components: [...w.components.filter((m) => m.socket !== socket), { socket, componentId }],
    }));
  },

  unmountComponent: (socket) => {
    updateActive(get, set, 'Unmount component', (w) => ({
      ...w,
      components: w.components.filter((m) => m.socket !== socket),
    }));
  },

  setOffsetOverride: (socket, offset) => {
    updateActive(get, set, 'Adjust offset', (w) => ({
      ...w,
      components: w.components.map((m) => (m.socket === socket ? { ...m, offsetOverride: offset } : m)),
    }));
  },

  setSkinPartFinish: (role, finish) => {
    updateActive(get, set, `Edit ${role} finish`, (w) => {
      const parts = { ...w.skin.parts };
      if (finish) parts[role] = finish;
      else delete parts[role];
      return { ...w, skin: { ...w.skin, parts } };
    });
  },

  renameActiveSkin: (name) => {
    updateActive(get, set, 'Rename skin', (w) => ({ ...w, skin: { ...w.skin, name } }));
  },

  applySkin: (skin) => {
    updateActive(get, set, `Apply "${skin.name}"`, (w) => ({ ...w, skin: cloneSkin(skin) }));
  },

  setAudioLayer: (layer, value) => {
    updateActive(get, set, `Edit ${layer} sample`, (w) => ({
      ...w,
      audio: { ...(w.audio ?? {}), [layer]: value },
    }));
  },

  setReverbSend: (value) => {
    updateActive(get, set, 'Adjust reverb send', (w) => ({
      ...w,
      audio: { ...(w.audio ?? {}), reverbSend: value },
    }));
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return;
    const [previous, ...rest] = state.undoStack as [UndoEntry, ...UndoEntry[]];
    const current = state.weapons.find((w) => w.id === previous.weaponId);
    if (!current) {
      set({ undoStack: rest });
      return;
    }
    set({
      weapons: state.weapons.map((w) => (w.id === previous.weaponId ? cloneWeapon(previous.snapshot) : w)),
      undoStack: rest,
      redoStack: [{ weaponId: previous.weaponId, snapshot: cloneWeapon(current), label: previous.label }, ...state.redoStack].slice(0, UNDO_LIMIT),
      statusMessage: `Undo: ${previous.label}`,
      saveState: 'idle',
    });
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return;
    const [next, ...rest] = state.redoStack as [UndoEntry, ...UndoEntry[]];
    const current = state.weapons.find((w) => w.id === next.weaponId);
    if (!current) {
      set({ redoStack: rest });
      return;
    }
    set({
      weapons: state.weapons.map((w) => (w.id === next.weaponId ? cloneWeapon(next.snapshot) : w)),
      redoStack: rest,
      undoStack: [{ weaponId: next.weaponId, snapshot: cloneWeapon(current), label: next.label }, ...state.undoStack].slice(0, UNDO_LIMIT),
      statusMessage: `Redo: ${next.label}`,
      saveState: 'idle',
    });
  },

  saveActiveWeapon: async () => {
    const state = get();
    const active = state.weapons.find((w) => w.id === state.activeWeaponId);
    if (!active) return;
    set({ saveState: 'saving', statusMessage: 'Saving...' });
    try {
      const saved = await getWeaponPersistence().saveWeapon(getActiveProjectId(), active);
      set((s) => ({
        weapons: s.weapons.map((w) => (w.id === saved.id ? saved : w)),
        sourceWeapons: [...s.sourceWeapons.filter((w) => w.id !== saved.id), cloneWeapon(saved)],
        undoStack: s.undoStack.filter((e) => e.weaponId !== saved.id),
        redoStack: s.redoStack.filter((e) => e.weaponId !== saved.id),
        saveState: 'saved',
        statusMessage: `Saved "${saved.name}".`,
      }));
    } catch (error) {
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to save.',
      });
    }
  },

  publishActiveWeapon: async () => {
    const state = get();
    const active = state.weapons.find((w) => w.id === state.activeWeaponId);
    if (!active) return;
    set({ saveState: 'saving', statusMessage: 'Publishing...' });
    try {
      // Publish always saves first: an unpublished edit must never go live
      // silently as a side effect of publish reading stale saved state.
      const saved = await getWeaponPersistence().saveWeapon(getActiveProjectId(), active);
      await getWeaponPersistence().publishWeapon(getActiveProjectId(), saved.id);
      set((s) => ({
        weapons: s.weapons.map((w) => (w.id === saved.id ? saved : w)),
        sourceWeapons: [...s.sourceWeapons.filter((w) => w.id !== saved.id), cloneWeapon(saved)],
        undoStack: s.undoStack.filter((e) => e.weaponId !== saved.id),
        redoStack: s.redoStack.filter((e) => e.weaponId !== saved.id),
        publishedWeaponIds: [...new Set([...s.publishedWeaponIds, saved.id])],
        saveState: 'saved',
        statusMessage: `Published "${saved.name}".`,
      }));
    } catch (error) {
      if (error instanceof WeaponPublishValidationError) {
        set({
          saveState: 'error',
          statusMessage: `Can't publish: ${error.errors.join('; ') || error.message}`,
        });
        return;
      }
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to publish.',
      });
    }
  },

  resetActiveToSource: () => {
    const state = get();
    const source = state.sourceWeapons.find((w) => w.id === state.activeWeaponId);
    if (!source) return;
    set({
      weapons: state.weapons.map((w) => (w.id === source.id ? cloneWeapon(source) : w)),
      undoStack: state.undoStack.filter((e) => e.weaponId !== source.id),
      redoStack: state.redoStack.filter((e) => e.weaponId !== source.id),
      selectedSocket: null,
      statusMessage: 'Reverted unsaved changes.',
      saveState: 'idle',
    });
  },

  clearStatus: () => set({ statusMessage: null }),
}));
